import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import {
  useAdminSources,
  useAdminStores,
  useCreateSource,
  useCreateStore,
  useUpdateSource,
  useUpdateStore,
} from '../lib/admin-api'

export const Route = createFileRoute('/admin/taxonomy')({
  component: TaxonomyPage,
})

type Store = NonNullable<ReturnType<typeof useAdminStores>['data']>[number]
type Source = NonNullable<ReturnType<typeof useAdminSources>['data']>[number]

/**
 * The two lookup tables behind every key: where it is redeemed, and where it
 * came from.
 *
 * Both are editable here because both accumulate mistakes in normal use —
 * a storefront added in a hurry with the wrong colour, a bundle whose name
 * was typed into the add-key box and is now attached to thirty keys.
 */
function TaxonomyPage() {
  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="font-display text-2xl font-semibold tracking-tight">Stores &amp; sources</h1>
      <p className="mt-1 text-sm text-ink-soft">
        Where keys are redeemed, and where they came from. Everything here is editable except a
        store's slug, which links and imports are matched against.
      </p>

      <Stores />
      <Sources />
    </div>
  )
}

/* --- stores --------------------------------------------------------------- */

function Stores() {
  const stores = useAdminStores()
  const create = useCreateStore()
  const [name, setName] = useState('')

  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="font-display text-lg font-semibold tracking-tight">Stores</h2>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            create.mutate({ name: name.trim() }, { onSuccess: () => setName('') })
          }}
          className="flex items-center gap-2"
        >
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Add a store…"
            aria-label="New store name"
            className="w-44 rounded-lg border border-line bg-surface-1 px-3 py-1.5 text-xs text-ink outline-none placeholder:text-ink-muted focus:border-accent"
          />
          <button
            type="submit"
            disabled={create.isPending || name.trim().length === 0}
            className="rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft transition-colors hover:border-accent hover:text-ink disabled:opacity-40"
          >
            Add
          </button>
        </form>
      </div>

      {create.isError ? (
        <p role="alert" className="mt-2 text-xs text-bad">
          {(create.error as Error).message}
        </p>
      ) : null}

      {stores.isPending ? (
        <p className="mt-4 text-sm text-ink-muted">Loading…</p>
      ) : (
        <ul className="mt-4 overflow-hidden rounded-xl border border-line">
          {(stores.data ?? []).map((store) => (
            <StoreRow key={store.id} store={store} />
          ))}
        </ul>
      )}
    </section>
  )
}

function StoreRow({ store }: { store: Store }) {
  const update = useUpdateStore(store.id)
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    name: store.name,
    brandColor: store.brandColor,
    urlTemplate: store.urlTemplate ?? '',
    sortOrder: store.sortOrder,
    isActive: store.isActive,
  })

  const save = (event: React.FormEvent) => {
    event.preventDefault()
    update.mutate(
      {
        name: form.name.trim(),
        brandColor: form.brandColor,
        urlTemplate: form.urlTemplate.trim() || null,
        sortOrder: form.sortOrder,
        isActive: form.isActive,
      },
      { onSuccess: () => setOpen(false) },
    )
  }

  return (
    <li className="border-b border-line last:border-b-0">
      <div className="flex items-center gap-3 bg-surface-1 px-3 py-2.5">
        <span
          className="size-3 shrink-0 rounded-full"
          style={{ backgroundColor: store.brandColor }}
        />
        <span className="text-sm text-ink">{store.name}</span>
        {!store.isActive ? (
          <span className="rounded-full bg-surface-3 px-2 py-0.5 text-[11px] text-ink-muted">
            hidden
          </span>
        ) : null}
        {/* Shown because it turns up in shareable filter links, and explained
            because it is the one field here that cannot be edited. */}
        <span
          className="font-mono text-[11px] text-ink-muted"
          title="Identity key: used in catalog filter links (?stores=steam) and to match store links coming from Steam and IGDB. Fixed once created."
        >
          {store.slug}
        </span>

        <span className="ml-auto font-mono text-[11px] text-ink-muted">
          {store.keyCount} {store.keyCount === 1 ? 'key' : 'keys'}
        </span>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="rounded px-2 py-1 text-[11px] text-ink-muted transition-colors hover:bg-surface-3 hover:text-ink"
        >
          {open ? 'Close' : 'Edit'}
        </button>
      </div>

      {open ? (
        <form onSubmit={save} className="border-t border-line bg-surface-0 p-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Name">
              <Input
                value={form.name}
                onChange={(value) => setForm({ ...form, name: value })}
              />
            </Field>

            <Field label="Colour">
              <ColorPicker
                value={form.brandColor}
                onChange={(value) => setForm({ ...form, brandColor: value })}
              />
            </Field>

            <Field label="Redeem URL template">
              <Input
                value={form.urlTemplate}
                onChange={(value) => setForm({ ...form, urlTemplate: value })}
                placeholder="https://…"
              />
            </Field>

            <Field label="Sort order">
              <Input
                value={String(form.sortOrder)}
                onChange={(value) =>
                  setForm({ ...form, sortOrder: Number(value.replace(/\D/g, '')) || 0 })
                }
                mono
              />
            </Field>
          </div>

          <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs text-ink-soft">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
              className="check"
            />
            Offer this store when adding keys
          </label>

          {update.isError ? (
            <p role="alert" className="mt-3 text-xs text-bad">
              {(update.error as Error).message}
            </p>
          ) : null}

          <SaveRow pending={update.isPending} onCancel={() => setOpen(false)} />
        </form>
      ) : null}
    </li>
  )
}

/* --- sources -------------------------------------------------------------- */

function Sources() {
  const sources = useAdminSources()
  const create = useCreateSource()
  const [name, setName] = useState('')

  return (
    <section className="mt-10">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="font-display text-lg font-semibold tracking-tight">Sources</h2>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            create.mutate({ name: name.trim() }, { onSuccess: () => setName('') })
          }}
          className="flex items-center gap-2"
        >
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Add a source…"
            aria-label="New source name"
            className="w-56 rounded-lg border border-line bg-surface-1 px-3 py-1.5 text-xs text-ink outline-none placeholder:text-ink-muted focus:border-accent"
          />
          <button
            type="submit"
            disabled={create.isPending || name.trim().length < 2}
            className="rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft transition-colors hover:border-accent hover:text-ink disabled:opacity-40"
          >
            Add
          </button>
        </form>
      </div>

      {create.isError ? (
        <p role="alert" className="mt-2 text-xs text-bad">
          {(create.error as Error).message}
        </p>
      ) : null}

      {sources.isPending ? (
        <p className="mt-4 text-sm text-ink-muted">Loading…</p>
      ) : (sources.data ?? []).length === 0 ? (
        <p className="mt-4 text-sm text-ink-soft">
          Nothing yet. Sources are also created as you type them into the add-key form.
        </p>
      ) : (
        <ul className="mt-4 overflow-hidden rounded-xl border border-line">
          {(sources.data ?? []).map((source) => (
            <SourceRow key={source.id} source={source} />
          ))}
        </ul>
      )}
    </section>
  )
}

function SourceRow({ source }: { source: Source }) {
  const update = useUpdateSource(source.id)
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    name: source.name,
    brandColor: source.brandColor,
    url: source.url ?? '',
    note: source.note ?? '',
    sortOrder: source.sortOrder,
  })

  const save = (event: React.FormEvent) => {
    event.preventDefault()
    update.mutate(
      {
        name: form.name.trim(),
        brandColor: form.brandColor,
        url: form.url.trim() || null,
        note: form.note.trim() || null,
        sortOrder: form.sortOrder,
      },
      { onSuccess: () => setOpen(false) },
    )
  }

  return (
    <li className="border-b border-line last:border-b-0">
      <div className="flex items-center gap-3 bg-surface-1 px-3 py-2.5">
        <span
          className="size-3 shrink-0 rounded-full"
          style={{ backgroundColor: source.brandColor }}
        />
        <span className="truncate text-sm text-ink">{source.name}</span>
        {source.note ? (
          <span className="truncate text-xs text-ink-muted italic" title={source.note}>
            {source.note}
          </span>
        ) : null}

        <span className="ml-auto font-mono text-[11px] text-ink-muted">
          {source.keyCount} {source.keyCount === 1 ? 'key' : 'keys'}
        </span>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="rounded px-2 py-1 text-[11px] text-ink-muted transition-colors hover:bg-surface-3 hover:text-ink"
        >
          {open ? 'Close' : 'Edit'}
        </button>
      </div>

      {open ? (
        <form onSubmit={save} className="border-t border-line bg-surface-0 p-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Name">
              <Input value={form.name} onChange={(value) => setForm({ ...form, name: value })} />
            </Field>

            <Field label="Colour">
              <ColorPicker
                value={form.brandColor}
                onChange={(value) => setForm({ ...form, brandColor: value })}
              />
            </Field>

            <Field label="Link">
              <Input
                value={form.url}
                onChange={(value) => setForm({ ...form, url: value })}
                placeholder="https://…"
              />
            </Field>

            <Field label="Sort order">
              <Input
                value={String(form.sortOrder)}
                onChange={(value) =>
                  setForm({ ...form, sortOrder: Number(value.replace(/\D/g, '')) || 0 })
                }
                mono
              />
            </Field>

            <Field label="Note" className="sm:col-span-2 lg:col-span-4">
              <Input value={form.note} onChange={(value) => setForm({ ...form, note: value })} />
            </Field>
          </div>

          {update.isError ? (
            <p role="alert" className="mt-3 text-xs text-bad">
              {(update.error as Error).message}
            </p>
          ) : null}

          <SaveRow pending={update.isPending} onCancel={() => setOpen(false)} />
        </form>
      ) : null}
    </li>
  )
}

/* --- shared bits ---------------------------------------------------------- */

const CONTROL =
  'w-full rounded-lg border border-line bg-surface-1 px-2.5 py-1.5 text-xs text-ink outline-none placeholder:text-ink-muted focus:border-accent'

/** A swatch and the hex beside it, so either way of picking works. */
function ColorPicker({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={(event) => onChange(event.target.value.toUpperCase())}
        aria-label="Colour"
        className="field-color size-8 shrink-0"
      />
      <Input value={value} onChange={onChange} mono />
    </div>
  )
}

function SaveRow({ pending, onCancel }: { pending: boolean; onCancel: () => void }) {
  return (
    <div className="mt-3 flex items-center gap-2">
      <button
        type="submit"
        disabled={pending}
        className="rounded-lg bg-accent px-3 py-1.5 text-xs font-semibold text-surface-0 transition-colors hover:bg-accent-strong disabled:opacity-40"
      >
        {pending ? 'Saving…' : 'Save'}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="rounded-lg px-3 py-1.5 text-xs text-ink-muted transition-colors hover:text-ink"
      >
        Cancel
      </button>
    </div>
  )
}

function Field({
  label,
  children,
  className = '',
}: {
  label: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block text-[11px] font-medium text-ink-muted">{label}</span>
      {children}
    </label>
  )
}

function Input({
  value,
  onChange,
  type = 'text',
  placeholder,
  mono = false,
}: {
  value: string
  onChange: (value: string) => void
  type?: string
  placeholder?: string
  mono?: boolean
}) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className={`${CONTROL} ${mono ? 'font-mono' : ''}`}
    />
  )
}
